import {
  Form,
  Modal,
  Spin,
  message,
  Upload,
  Switch,
  Col,
  Row
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import api from "../../hooks/api";
import axiosInstance from "../../axios/axios-instance";
import { pathAPI } from "../../utils/pathApi";
import { fileToBlob } from "../../utils/utils";
import "./index.scss";
import { Box } from "@mui/material";
import ActionButton from "../common/buttons/ActionButton";
import CancelButton from "../common/buttons/CancelButton";
import InputField from "../common/fields/InputField";
import SelectField from "../common/fields/SelectField";
import { FormInstance } from "antd/lib";
import { Typography } from "@mui/material";
import { currency_list } from "../../utils/constants";

interface LanguageOption {
  id: number;
  name: string;
  language_code: string;
}

interface CountryData {
  id?: number;
  name: string;
  code: string;
  flag?: string;
  currency: string;
  phone_code: string;
  language_id: number;
  active: boolean;
}

interface AddCountryProps {
  open: boolean;
  onCancel: () => void;
  reloadData: () => void;
  countryData?: CountryData;
}

const AddCountry: React.FC<AddCountryProps> = ({
  reloadData,
  onCancel,
  countryData,
  ...props
}) => {
  const { t } = useTranslation();
  const formRef = useRef<FormInstance>(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [languages, setLanguages] = useState<LanguageOption[]>([]);
  const [flagImage, setFlagImage] = useState<File | null>(null);
  const [fileList, setFileList] = useState<any[]>([]);
  const { Dragger } = Upload;
  const [messageApi, contextHolder] = message.useMessage();
  // Bumps per open so a late response from a previous item is ignored.
  const requestSeqRef = useRef(0);

  // Fetch languages and country data when modal opens
  useEffect(() => {
    if (!props.open) return;
    const seq = ++requestSeqRef.current;
    form.resetFields();
    fetchLanguages(seq);

    if (countryData?.id) {
      setFormLoading(true);
      fetchCountryDetails(countryData.id, seq);
    } else {
      setFormLoading(false);
    }
  }, [props.open, countryData?.id]);

  // Fetch language options (simplified, no loading state)
  const fetchLanguages = async (seq: number) => {
    try {
      const response = await axiosInstance.get(pathAPI.languages);
      if (seq !== requestSeqRef.current) return;
      if (response.data?.data) {
        setLanguages(response.data.data);
      }
    } catch (error) {
      if (seq !== requestSeqRef.current) return;
      console.error("Error fetching languages:", error);
      messageApi.error(t("country.form.lang_error"));
    }
  };

  // Fetch country details
  const fetchCountryDetails = async (id: number, seq: number) => {
    try {
      const response = await axiosInstance.get(`${pathAPI.countries}/${id}`);
      if (seq !== requestSeqRef.current) return;
      if (response.data?.data) {
        const country = response.data.data;
        form.setFieldsValue({
          name: country.name,
          code: country.code,
          phone_code: country.phone_code,
          language_id: country.language_id,
          currency: country.currency,
          active: country.active
        });
      }
    } catch (error) {
      if (seq !== requestSeqRef.current) return;
      console.error("Error fetching country details:", error);
      messageApi.error(t("country.form.detail_error"));
    } finally {
      if (seq === requestSeqRef.current) setFormLoading(false);
    }
  };

  // Save country data
  const saveCountry = async (data: FormData) => {
    try {
      setLoading(true);
      
      const xhr = new XMLHttpRequest();
      const token = localStorage.getItem("token");
      const baseUrl = process.env.REACT_APP_SERVER_DEV_ENDPOINT || '';
      const apiVersion = process.env.REACT_APP_API_VERSION || '';
      const url = countryData?.id 
        ? `${baseUrl}/${apiVersion}/${pathAPI.countries}/${countryData.id}`
        : `${baseUrl}/${apiVersion}/${pathAPI.countries}`;
      
      xhr.open(countryData?.id ? 'PUT' : 'POST', url);
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      
      xhr.onload = function() {
        setLoading(false);
        
        if (xhr.status >= 200 && xhr.status < 300) {
          messageApi.success(t(countryData?.id ? "country.form.updated_ok" : "country.form.created_ok"));
          reloadData();
          onCancel();
          form.resetFields();
          setFlagImage(null);
          setFileList([]);
        } else {
          console.error("Save error:", xhr.status, xhr.responseText);
          messageApi.error(t(countryData?.id ? "country.form.update_server_error" : "country.form.create_server_error"));
        }
      };
      
      xhr.onerror = function() {
        setLoading(false);
        messageApi.error(t(countryData?.id ? "country.form.update_network_error" : "country.form.create_network_error"));
      };
      
      xhr.send(data);
    } catch (error) {
      setLoading(false);
      console.error("Save error:", error);
      messageApi.error(t(countryData?.id ? "country.form.update_error" : "country.form.create_error"));
    }
  };

  // Form submission handler
  const onFinish = async () => {
    try {
      await form.validateFields();
      
      const formData = new FormData();
      
      // Create model object with form values
      const modelObj = {
        name: form.getFieldValue("name"),
        code: form.getFieldValue("code"),
        phone_code: form.getFieldValue("phone_code"),
        language_id: form.getFieldValue("language_id"),
        currency: form.getFieldValue("currency"),
        active: form.getFieldValue("active") === true
      };
      
      // Add flag file if selected
      if (flagImage && flagImage instanceof File) {
        formData.append('flagFile', flagImage, flagImage.name || 'flag.png');
      }
      
      // Add data as application/json blob
      formData.append('data', new Blob([JSON.stringify(modelObj)], {
        type: 'application/json'
      }));
      
      // Save country data
      saveCountry(formData);
    } catch (error) {
      console.error("Error in form submission:", error);
    }
  };

  // Close modal handler
  const handleCloseModal = () => {
    form.resetFields();
    setFlagImage(null);
    setFileList([]);
    onCancel();
  };

  return (
    <Modal
      title={countryData?.id ? t("country.form.edit_title") : t("country.form.add_title")}
      open={props.open}
      onCancel={handleCloseModal}
      width={600}
      footer={
        <Box display="flex" justifyContent="flex-end" gap={1.5}>
          <CancelButton onClick={handleCloseModal}>{t("btn.cancel")}</CancelButton>
          <ActionButton onClick={onFinish} loading={loading} disabled={formLoading}>
            {countryData?.id ? t("country.form.update") : t("country.form.create")}
          </ActionButton>
        </Box>
      }
    >
      {contextHolder}
      {formLoading ? (
        <div style={{ textAlign: 'center', padding: '30px' }}>
          <Spin size="large" tip={t("country.form.loading")} />
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          initialValues={{ active: true }}
        >
          <Row gutter={[16, 16]}>
            {/* Basic fields */}
            <Col span={12}>
              <Form.Item
                name="name"
                label={t("country.form.name")}
                rules={[{ required: true, message: t("country.form.name_required") }]}
              >
                <InputField placeholder={t("country.form.name_ph")} />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="code"
                label={t("country.form.code")}
                rules={[{ required: true, message: t("country.form.code_required") }]}
              >
                <InputField placeholder={t("country.form.code_ph")} />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="currency"
                label={t("country.form.currency")}
                rules={[{ required: true, message: t("country.form.currency_required") }]}
              >
                <SelectField
                  placeholder={t("country.form.currency_ph")}
                  options={currency_list.map((item) => ({
                    value: item.value,
                    label: `${item.name} (${item.value})`,
                  }))}
                />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="phone_code"
                label={t("country.form.phone_code")}
                rules={[{ required: true, message: t("country.form.phone_code_required") }]}
              >
                <InputField placeholder={t("country.form.phone_code_ph")} />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="language_id"
                label={t("country.form.language")}
                rules={[{ required: true, message: t("country.form.language_required") }]}
              >
                <SelectField
                  placeholder={t("country.form.language_ph")}
                  searchable={false}
                  matchAnchorWidth
                  options={languages.map((lang) => ({
                    value: lang.id,
                    label: `${lang.name} (${lang.language_code})`,
                  }))}
                />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="active"
                label={t("country.form.status")}
                valuePropName="checked"
                initialValue={true}
              >
                <Switch 
                  checkedChildren={t("country.form.active")} 
                  unCheckedChildren={t("country.form.inactive")} 
                />
              </Form.Item>
            </Col>
            
            {/* Flag Upload */}
            <Col span={24}>
              <Form.Item
                name="flag"
                label={t("country.form.flag")}
                rules={[
                  {
                    required: !countryData?.id,
                    message: t("country.form.flag_required"),
                  },
                ]}
              >
                <div>
                  {/* Display current flag if editing */}
                  {countryData?.flag && (
                    <div style={{ marginBottom: '15px' }}>
                      <img 
                        src={countryData.flag} 
                        alt={t("country.form.flag_alt")} 
                        style={{ 
                          maxWidth: '150px',
                          height: '100px',
                          objectFit: 'contain' as const,
                          border: '1px solid var(--app-border-light)',
                          borderRadius: '4px',
                          padding: '4px',
                          marginBottom: '10px'
                        }} 
                      />
                    </div>
                  )}
                  
                  {/* Upload control */}
                  <Dragger
                    fileList={fileList}
                    multiple={false}
                    beforeUpload={(file) => {
                      return false; // Prevent auto-upload
                    }}
                    onChange={(info) => {
                      // Update the fileList for the UI
                      setFileList(info.fileList.slice(-1));
                      
                      // Store the actual File object for submission
                      if (info.file.originFileObj) {
                        setFlagImage(info.file.originFileObj);
                      } else if (info.file instanceof File) {
                        setFlagImage(info.file);
                      }
                    }}
                    onRemove={() => {
                      setFileList([]);
                      setFlagImage(null);
                      return true;
                    }}
                    accept="image/*"
                    listType="picture"
                  >
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">
                      {t("country.form.drag_hint")}
                    </p>
                    <p className="ant-upload-hint">
                      {countryData?.id ? t("country.form.upload_optional") : t("country.form.upload_required")}
                    </p>
                  </Dragger>
                </div>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      )}
    </Modal>
  );
};

export default AddCountry;
